import { Icons, type LBIcon } from '@/assets/icons/icons';

import {
  ContextCollectionState,
  type ContextCollectionStatus,
  type ContextCollectionTimerState,
} from './context-collection-provider';
import { CONTEXT_COLLECTOR_STATE } from './context-collection.types';

export const mapToUIState = (
  status: ContextCollectionStatus,
  accessibilityPermissions: boolean | null,
  supportsContextCollection: boolean,
  isStarting: boolean,
  isStopping: boolean
): CONTEXT_COLLECTOR_STATE => {
  if (!supportsContextCollection) {
    return CONTEXT_COLLECTOR_STATE.UNSUPPORTED;
  }

  if (accessibilityPermissions === false) {
    return CONTEXT_COLLECTOR_STATE.ACCESSIBILITY_PERMISSION_MISSING;
  }

  if (isStarting) return CONTEXT_COLLECTOR_STATE.STARTING;
  if (isStopping) return CONTEXT_COLLECTOR_STATE.STOPPING;

  switch (status.state) {
    case ContextCollectionState.LOADING:
    case ContextCollectionState.SPAWNING:
      return CONTEXT_COLLECTOR_STATE.STARTING;

    case ContextCollectionState.RUNNING:
      return CONTEXT_COLLECTOR_STATE.ENABLED;

    case ContextCollectionState.PAUSED:
      return CONTEXT_COLLECTOR_STATE.PAUSED_TIMED;

    case ContextCollectionState.STOPPING:
      return CONTEXT_COLLECTOR_STATE.STOPPING;

    case ContextCollectionState.DISABLED:
      return CONTEXT_COLLECTOR_STATE.STOPPED;

    case ContextCollectionState.STOPPED:
      return CONTEXT_COLLECTOR_STATE.STOPPED;

    case ContextCollectionState.ERROR:
      if (status.error?.includes('start')) {
        return CONTEXT_COLLECTOR_STATE.UNABLE_TO_START;
      } else if (status.error?.includes('stop')) {
        return CONTEXT_COLLECTOR_STATE.UNABLE_TO_STOP;
      } else {
        return CONTEXT_COLLECTOR_STATE.SOMETHING_WENT_WRONG;
      }

    default:
      return CONTEXT_COLLECTOR_STATE.STOPPED;
  }
};

export interface ContextCollectionStateDetails {
  iconColor: string;
  icon?: LBIcon;
  text: string;
}

const humanizeRemainingMs = (ms: number): string => {
  if (ms <= 0) return '0s';
  if (ms < 60_000) return `${Math.ceil(ms / 1000)}s`;
  const totalMinutes = Math.ceil(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

export const getStateDetails = (
  contextCollectionState: CONTEXT_COLLECTOR_STATE,
  timerState?: ContextCollectionTimerState
): ContextCollectionStateDetails => {
  switch (contextCollectionState) {
    case CONTEXT_COLLECTOR_STATE.ENABLED:
      return { iconColor: 'bg-green-600', text: 'Context enabled' };

    case CONTEXT_COLLECTOR_STATE.PAUSED_TIMED: {
      const remaining = timerState?.remainingTimeMs;
      const text =
        remaining && remaining > 0
          ? `Paused for ${humanizeRemainingMs(remaining)}`
          : 'Context paused';
      return { iconColor: 'bg-gray-8', icon: Icons.pauseFilled, text };
    }

    case CONTEXT_COLLECTOR_STATE.STOPPED:
      return { iconColor: 'bg-gray-8', text: 'Context disabled' };

    case CONTEXT_COLLECTOR_STATE.ACCESSIBILITY_PERMISSION_MISSING:
      return { iconColor: 'bg-foreground', text: 'Grant Permissions' };

    case CONTEXT_COLLECTOR_STATE.STARTING:
      return { iconColor: 'bg-warning-9', text: 'Enabling context...' };

    case CONTEXT_COLLECTOR_STATE.STOPPING:
      return { iconColor: 'bg-warning-9', text: 'Disabling context...' };

    case CONTEXT_COLLECTOR_STATE.SOMETHING_WENT_WRONG:
      return { iconColor: 'bg-danger-9', text: 'Something went wrong' };

    case CONTEXT_COLLECTOR_STATE.UNABLE_TO_START:
      return { iconColor: 'bg-danger-9', text: 'Failed to Start' };

    case CONTEXT_COLLECTOR_STATE.UNABLE_TO_STOP:
      return { iconColor: 'bg-danger-9', text: 'Failed to Stop' };

    case CONTEXT_COLLECTOR_STATE.UNSUPPORTED:
      return { iconColor: 'bg-foreground', text: 'Coming Soon' };
  }
};
